import { ReactNode, useEffect, useState } from 'react';

import {
  EditOutlined,
  InfoOutlined,
  NotificationsActive,
  NotificationsNone,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  addFavorite,
  findFavoriteByType,
  removeFavorite,
} from 'src/services/favorite';
import { Alert, Status } from 'src/types/alert';
import { Favorite } from 'src/types/favorite';
import { useScript } from 'src/utils/hooks';
import { truncateText } from 'src/utils/string';

import { AlertDialog } from './AlertDialog';

type FavoriteWithAlerts = {
  favorite: Favorite;
  alerts: Alert[];
};

export interface OperationSubheaderProps {
  title?: string;
  market?: string;
  breadcrumbs1?: string;
  breadcrumbs2?: string;
  tooltipText?: string;
  disableFavorite?: boolean;
  disableAlerts?: boolean;
  entityId?: number | string | null;
  actions?: ReactNode;
}

const FAVORITE_TYPE = 'operation';

export default function OperationSubheader({
  title,
  market,
  breadcrumbs1,
  breadcrumbs2,
  tooltipText,
  disableFavorite = false,
  disableAlerts = false,
  entityId = null,
  actions = null,
}: OperationSubheaderProps) {
  useScript('https://tally.so/widgets/embed.js');
  const theme = useTheme();
  const { auth } = useAuthContext();
  const { hashId } = useParams<{ hashId: string }>();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const hasAlertPermission = Boolean(
    auth?.user?.subscription?.plan?.permissions?.alertas,
  );

  const { data: favoriteResponse, refetch } = useQuery<{
    data: FavoriteWithAlerts[];
  }>(
    ['favorite', FAVORITE_TYPE],
    () =>
      findFavoriteByType({
        [FAVORITE_TYPE]: hashId,
      } as Parameters<typeof findFavoriteByType>[0]),
    {
      enabled: false,
    },
  );

  const favoriteItem = favoriteResponse?.data?.[0];
  const isFavorited =
    !!favoriteItem && favoriteItem.favorite?.deletedAt === null;
  const hasAlerts = (favoriteItem?.alerts?.length ?? 0) > 0;
  const hasEnabledAlerts =
    favoriteItem?.alerts?.some((alert) => alert.status === Status.ENABLED) ??
    false;

  const addFavoriteMutation = useMutation(addFavorite, {
    onSuccess: () => refetch(),
  });
  const { isLoading: addFavoriteLoading } = addFavoriteMutation;

  const removeFavoriteMutation = useMutation(removeFavorite, {
    onSuccess: () => refetch(),
  });
  const { isLoading: removeFavoriteLoading } = removeFavoriteMutation;

  const isMutatingFavorite = addFavoriteLoading || removeFavoriteLoading;

  useEffect(() => {
    if (auth?.user?.id) {
      refetch();
    }
  }, [auth?.user, refetch]);

  const handleAddFavorite = () => {
    if (!isMutatingFavorite) {
      addFavoriteMutation.mutate({
        data: {
          user: auth?.user?.id,
          [FAVORITE_TYPE]: Number(entityId ?? hashId),
        },
      });
    }
  };

  const handleRemoveFavorite = () => {
    if (!isMutatingFavorite && favoriteItem?.favorite?.id) {
      removeFavoriteMutation.mutate(favoriteItem.favorite.id);
    }
  };

  const tooltipSx = isExtraSmall
    ? { maxWidth: '98%', p: 2, fontSize: 14 }
    : { maxWidth: 600, p: 2, fontSize: 12 };

  return (
    <Box sx={{ width: '100%', py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2,
        }}>
        <Box sx={{ p: 1, maxWidth: { xs: '100%', lg: '70%' } }}>
          {!isExtraSmall && (breadcrumbs1 || breadcrumbs2) && (
            <Breadcrumbs
              aria-label="navegação-secundária"
              sx={{ fontSize: '0.875rem' }}>
              <Link
                component={RouterLink}
                to="/plataforma"
                underline="none"
                sx={{ color: 'primary.main' }}>
                Home
              </Link>
              <Typography variant="body2">{breadcrumbs1}</Typography>
              {breadcrumbs2 && (
                <Typography variant="body2" title={breadcrumbs2}>
                  {truncateText(breadcrumbs2, 30)}
                </Typography>
              )}
            </Breadcrumbs>
          )}
          <Typography
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              fontWeight: 400,
              lineHeight: 1.235,
              display: 'flex',
              alignItems: 'center',
            }}>
            {title}
            {tooltipText && (
              <Tooltip
                enterTouchDelay={10}
                arrow
                placement={isExtraSmall ? 'bottom' : 'right'}
                title={tooltipText}
                componentsProps={{ tooltip: { sx: tooltipSx } }}>
                <IconButton aria-label="Mais informações">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          {market && (
            <Typography sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              {market}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            p: 1,
            gap: 1,
          }}>
          {auth?.isLoggedIn && (
            <Tooltip
              enterTouchDelay={10}
              arrow
              placement={isExtraSmall ? 'bottom' : 'top'}
              title={
                isFavorited
                  ? 'Remover dos meus favoritos'
                  : 'Adicionar nos meus favoritos'
              }
              componentsProps={{ tooltip: { sx: tooltipSx } }}>
              {isFavorited ? (
                <IconButton
                  aria-label="Remover dos meus favoritos"
                  onClick={handleRemoveFavorite}
                  disabled={isMutatingFavorite}>
                  <Star fontSize="small" color="primary" />
                </IconButton>
              ) : (
                <IconButton
                  aria-label="Adicionar nos meus favoritos"
                  onClick={handleAddFavorite}
                  disabled={isMutatingFavorite || disableFavorite}>
                  <StarBorder
                    fontSize="small"
                    color={disableFavorite ? 'disabled' : 'primary'}
                  />
                </IconButton>
              )}
            </Tooltip>
          )}
          {!disableAlerts && (
            <Tooltip
              enterTouchDelay={10}
              arrow
              placement={isExtraSmall ? 'bottom' : 'top'}
              title={
                hasAlertPermission
                  ? 'Alertas'
                  : 'Funcionalidade indisponível para o plano atual'
              }
              componentsProps={{ tooltip: { sx: tooltipSx } }}>
              <span>
                <IconButton
                  aria-label="Alertas"
                  disabled={!isFavorited || !hasAlertPermission}
                  onClick={() => setOpenAlertDialog(true)}>
                  {hasAlerts && hasEnabledAlerts && hasAlertPermission ? (
                    <NotificationsActive fontSize="small" color="primary" />
                  ) : (
                    <NotificationsNone
                      fontSize="small"
                      color={
                        isFavorited && hasAlertPermission
                          ? 'primary'
                          : 'disabled'
                      }
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip
            enterTouchDelay={10}
            arrow
            placement={isExtraSmall ? 'bottom' : 'top'}
            title="Faltou algo? Conte para nós"
            componentsProps={{ tooltip: { sx: tooltipSx } }}>
            <IconButton
              aria-label="Faltou algo? Conte para nós"
              onClick={() => {
                window.Tally?.openPopup('w5XL9b', {
                  width: 640,
                  autoClose: 2000,
                  layout: 'modal',
                  overlay: true,
                  hiddenFields: {
                    mercado: market,
                    operacao: title,
                  },
                });
              }}>
              <EditOutlined color="primary" />
            </IconButton>
          </Tooltip>
          {actions}
        </Box>
      </Box>
      <Divider sx={{ height: 4, bgcolor: 'primary.main' }} />
      {favoriteItem?.favorite && (
        <AlertDialog
          title="Alertas da operação"
          open={openAlertDialog}
          onClose={() => {
            setOpenAlertDialog(false);
            refetch();
          }}
          favorite={favoriteItem.favorite}
        />
      )}
    </Box>
  );
}
